import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import config from '../../Config';
import axios from 'axios';
import Spinner from 'react-bootstrap/Spinner';
import PayrollMenu from './PayrollMenu';
import Pagination from '../General/Pagination';
import moment from 'moment';
import { scrollToTop } from '../Shared/ScrollOnTop';

const norm = (value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'boolean') return value ? 'true' : 'false';
    return String(value).trim();
};

const getAuditInfo = (record) => {
    if (!record || !record.ID) {
        return { modifiedBy: '—', modifiedOn: '—' };
    }

    let modifiedBy = '—';
    const remarks = record.Remarks || '';
    const updatedMatch = remarks.match(/Updated By:\s*(.+?)\s+ID:\s*(\d+)/i);
    if (updatedMatch) {
        modifiedBy = `${updatedMatch[1].trim()} (ID: ${updatedMatch[2]})`;
    } else if (remarks) {
        modifiedBy = remarks;
    }

    const modifiedOn = record.Updated_Date
        ? moment(record.Updated_Date).format('DD/MM/YYYY, h:mm:ss a')
        : record.Created_Date
            ? moment(record.Created_Date).format('DD/MM/YYYY, h:mm:ss a')
            : '—';

    return { modifiedBy, modifiedOn };
};

const fieldValue = (record, field) => {
    if (!record) return '';
    switch (field) {
        case 'DesignationID':
            return record.Selected_Designation?.ID ?? '';
        case 'DesignationName':
            return record.Selected_Designation?.Name ?? '';
        case 'EmployeeTypeID':
            return record.Employee_Type_BE?.ID ?? record.Employee_Type ?? '';
        case 'EmployeeTypeName':
            return record.Employee_Type_BE?.Name ?? '';
        case 'Gender':
            return record.Gender;
        case 'Date_Of_Birth':
            return record.Date_Of_Birth ? moment(record.Date_Of_Birth).format('YYYY-MM-DD') : '';
        case 'Date_Of_Joining':
            return record.Date_Of_Joining ? moment(record.Date_Of_Joining).format('YYYY-MM-DD') : '';
        default:
            return record[field] ?? '';
    }
};

const EmployeeHistory = () => {
    const params = useParams();
    const [EmployeeID] = useState(params.id);
    const [AccessKey] = useState(localStorage.getItem('AccessKey'));
    const [UserID] = useState(localStorage.getItem('ID'));
    const [MemberType] = useState(localStorage.getItem('MemberType'));

    const [Employee, setEmployee] = useState({});
    const [PreviousRecord, setPreviousRecord] = useState(null);
    const [loading, setLoading] = useState(true);

    const [pageNo, setPageNo] = useState(1);
    const [pageSize] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);

    const isChanged = (field) => {
        if (!PreviousRecord || !Employee) return false;
        return norm(fieldValue(Employee, field)) !== norm(fieldValue(PreviousRecord, field));
    };

    const changedStyle = (field) => (isChanged(field) ? { color: '#dc3545', borderColor: '#dc3545' } : {});

    const handlePageChange = (page) => {
        scrollToTop();
        setPageNo(page);
    };

    useEffect(() => {
        document.title = 'Employee Modification History';
        setLoading(true);

        const data = {
            AccessKey,
            UserID,
            MemberType,
            pageNo,
            pageSize,
            ID: EmployeeID,
        };

        axios({
            method: 'post',
            url: config.base_url + 'Payroll/GetStaffHistory',
            headers: { 'Content-Type': 'application/json' },
            data,
        })
            .then((response) => {
                if (response.data.status_code === 1 && response.data.ListofRecords?.length > 0) {
                    setEmployee(response.data.ListofRecords[0]);
                    setPreviousRecord(response.data.PreviousRecord || null);
                    setTotalRecords(response.data.totalRecords);
                } else {
                    setEmployee({});
                    setPreviousRecord(null);
                    setTotalRecords(0);
                }
                setLoading(false);
            })
            .catch(() => {
                setLoading(false);
            });
    }, [pageNo, pageSize, EmployeeID, AccessKey, UserID, MemberType]);

    const genderLabel = (value) => {
        if (value === true || value === 'true') return 'Male';
        if (value === false || value === 'false') return 'Female';
        return '';
    };

    const auditInfo = getAuditInfo(Employee);

    return (
        <>
            <PayrollMenu />
            <div className="page-head">
                <div className="container">
                    <div className="row align-items-center">
                        <div className="col-md-6">
                            <nav aria-label="breadcrumb">
                                <ol className="breadcrumb">
                                    <li className="breadcrumb-item"><Link to="/Dashboard">Dashboard</Link></li>
                                    <li className="breadcrumb-item"><Link to="/PayRoll/PayRollDashboard">Pay Roll</Link></li>
                                    <li className="breadcrumb-item"><Link to={`/PayRoll/EditEmployee/${EmployeeID}`}>Edit Employee</Link></li>
                                    <li className="breadcrumb-item active" aria-current="page">Modification History</li>
                                </ol>
                            </nav>
                            <div className="page-title">Employee Modification History</div>
                        </div>
                        <div className="col-md-6">
                            <div className="page-right-actions">
                                <Link to={`/PayRoll/EditEmployee/${EmployeeID}`} className="button button-white button-icon">
                                    Back to Edit Employee
                                    <i className="ri-arrow-left-line"></i>
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="page-content">
                <div className="container">
                    <div className="card mb-3">
                        <div className="card-body py-2">
                            <small className="text-muted">
                                Fields highlighted in <span style={{ color: '#dc3545' }}>red</span> were changed in this modification compared to the previous saved version.
                            </small>
                        </div>
                    </div>
                    <div className="card">
                        <div className="card-body p-md-4">
                            {loading ? (
                                <div className="text-center">
                                    <Spinner animation="border" role="status" />
                                </div>
                            ) : totalRecords === 0 ? (
                                <div className="text-center text-muted">No modification history found for this employee.</div>
                            ) : (
                                <>
                                    <div className="form-wrap mb-0">
                                        <div className="form-legend" style={{ left: '64px' }}>Modification Details</div>
                                        <div className="row align-items-center">
                                            <div className="col-md-4">
                                                <div className="form-group mb-3">
                                                    <label>Modification No.</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        readOnly
                                                        disabled
                                                        value={`${pageNo} of ${totalRecords}`}
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-md-4">
                                                <div className="form-group mb-3">
                                                    <label>Modified By</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        readOnly
                                                        disabled
                                                        value={auditInfo.modifiedBy}
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-md-4">
                                                <div className="form-group mb-3">
                                                    <label>Modified On</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        readOnly
                                                        disabled
                                                        value={auditInfo.modifiedOn}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="form-wrap">
                                        <div className="form-legend" style={{ left: '64px' }}>Basic Information</div>
                                        <div className="row align-items-center">
                                            <div className="col-md-9">
                                                <div className="row align-item-center">
                                                    <div className="col-md-3">
                                                        <div className="form-group mb-3">
                                                            <label>Employee Code:</label>
                                                            <div className="label-gray">{Employee.Code}</div>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-3">
                                                        <div className="form-group mb-3">
                                                            <label>Employee Name:</label>
                                                            <input type="text" className="form-control" readOnly disabled value={Employee.Name || ''} style={changedStyle('Name')} />
                                                        </div>
                                                    </div>
                                                    <div className="col-md-3">
                                                        <div className="form-group mb-3">
                                                            <label>Father Name:</label>
                                                            <input type="text" className="form-control" readOnly disabled value={Employee.Father_Name || ''} style={changedStyle('Father_Name')} />
                                                        </div>
                                                    </div>
                                                    <div className="col-md-3">
                                                        <div className="form-group mb-3">
                                                            <label>Husband Name:</label>
                                                            <input type="text" className="form-control" readOnly disabled value={Employee.Husband_Name || ''} style={changedStyle('Husband_Name')} />
                                                        </div>
                                                    </div>
                                                    <div className="col-md-3">
                                                        <div className="form-group mb-3">
                                                            <label>Marital Status:</label>
                                                            <input type="text" className="form-control" readOnly disabled value={Employee.Marital_Status || ''} style={changedStyle('Marital_Status')} />
                                                        </div>
                                                    </div>
                                                    <div className="col-md-3">
                                                        <div className="form-group mb-3">
                                                            <label>Gender:</label>
                                                            <input type="text" className="form-control" readOnly disabled value={genderLabel(Employee.Gender)} style={changedStyle('Gender')} />
                                                        </div>
                                                    </div>
                                                    <div className="col-md-3">
                                                        <div className="form-group mb-3">
                                                            <label>Date of Birth:</label>
                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                readOnly
                                                                disabled
                                                                value={Employee.Date_Of_Birth ? moment(Employee.Date_Of_Birth).format('DD/MM/YYYY') : ''}
                                                                style={changedStyle('Date_Of_Birth')}
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="col-md-3">
                                                        <div className="form-group mb-3">
                                                            <label>Blood Group:</label>
                                                            <input type="text" className="form-control" readOnly disabled value={Employee.Blood_Group || ''} style={changedStyle('Blood_Group')} />
                                                        </div>
                                                    </div>
                                                    <div className="col-md-3">
                                                        <div className="form-group mb-3">
                                                            <label>CNIC No:</label>
                                                            <input type="text" className="form-control" readOnly disabled value={Employee.CNIC_NO || ''} style={changedStyle('CNIC_NO')} />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-md-3 text-center">
                                                <img className="mb-3" src={config.StaffImagePath + Employee.Code + '.jpg'} style={{ width: '150px' }} alt="" />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="form-wrap">
                                        <div className="form-legend" style={{ left: '64px' }}>Account Information</div>
                                        <div className="row align-items-center">
                                            <div className="col-md-3">
                                                <div className="form-group mb-3">
                                                    <label>Bank Account No:</label>
                                                    <input type="text" className="form-control" readOnly disabled value={Employee.Account_No || ''} style={changedStyle('Account_No')} />
                                                </div>
                                            </div>
                                            <div className="col-md-3">
                                                <div className="form-group mb-3">
                                                    <label>EOBI No:</label>
                                                    <input type="text" className="form-control" readOnly disabled value={Employee.EOBI_NO || ''} style={changedStyle('EOBI_NO')} />
                                                </div>
                                            </div>
                                            <div className="col-md-3">
                                                <div className="form-group mb-3">
                                                    <label>NTN NO:</label>
                                                    <input type="text" className="form-control" readOnly disabled value={Employee.NTN_NO || ''} style={changedStyle('NTN_NO')} />
                                                </div>
                                            </div>
                                            <div className="col-md-3">
                                                <div className="form-group mb-3">
                                                    <label>Password:</label>
                                                    <input type="text" className="form-control" readOnly disabled value={Employee.Password || ''} style={changedStyle('Password')} />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="form-wrap">
                                        <div className="form-legend" style={{ left: '64px' }}>Contact Information</div>
                                        <div className="row align-items-center">
                                            <div className="col-md-3">
                                                <div className="form-group mb-3">
                                                    <label>Phone No:</label>
                                                    <input type="text" className="form-control" readOnly disabled value={Employee.Phone_No || ''} style={changedStyle('Phone_No')} />
                                                </div>
                                            </div>
                                            <div className="col-md-3">
                                                <div className="form-group mb-3">
                                                    <label>Mobile No:</label>
                                                    <input type="text" className="form-control" readOnly disabled value={Employee.Mobile_No || ''} style={changedStyle('Mobile_No')} />
                                                </div>
                                            </div>
                                            <div className="col-md-3">
                                                <div className="form-group mb-3">
                                                    <label>Date of Joining:</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        readOnly
                                                        disabled
                                                        value={Employee.Date_Of_Joining ? moment(Employee.Date_Of_Joining).format('DD/MM/YYYY') : ''}
                                                        style={changedStyle('Date_Of_Joining')}
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-md-3">
                                                <div className="form-group mb-3">
                                                    <label>Designation:</label>
                                                    <input type="text" className="form-control" readOnly disabled value={fieldValue(Employee, 'DesignationName')} style={changedStyle('DesignationID')} />
                                                </div>
                                            </div>
                                            <div className="col-md-3">
                                                <div className="form-group mb-3">
                                                    <label>Email Address:</label>
                                                    <input type="text" className="form-control" readOnly disabled value={Employee.Email_Address || ''} style={changedStyle('Email_Address')} />
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="form-group mb-3">
                                                    <label>Home Address:</label>
                                                    <input type="text" className="form-control" readOnly disabled value={Employee.Home_Address || ''} style={changedStyle('Home_Address')} />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="form-wrap">
                                        <div className="form-legend" style={{ left: '64px' }}>Pay / Qualification Information</div>
                                        <div className="row align-items-center">
                                            <div className="col-md-3">
                                                <div className="form-group mb-3">
                                                    <label>Basic Pay Scale</label>
                                                    <input type="text" className="form-control" readOnly disabled value={Employee.Basic_Pay_Scale || ''} style={changedStyle('Basic_Pay_Scale')} />
                                                </div>
                                            </div>
                                            <div className="col-md-3">
                                                <div className="form-group mb-3">
                                                    <label>Pay Scale GPS:</label>
                                                    <input type="text" className="form-control" readOnly disabled value={Employee.GPS_Scale || ''} style={changedStyle('GPS_Scale')} />
                                                </div>
                                            </div>
                                            <div className="col-md-3">
                                                <div className="form-group mb-3">
                                                    <label>Group/Depart:</label>
                                                    <input type="text" className="form-control" readOnly disabled value={fieldValue(Employee, 'EmployeeTypeName')} style={changedStyle('EmployeeTypeID')} />
                                                </div>
                                            </div>
                                            <div className="col-md-12">
                                                <div className="form-group mb-3">
                                                    <label>Qualification:</label>
                                                    <input type="text" className="form-control" readOnly disabled value={Employee.Qualification || ''} style={changedStyle('Qualification')} />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                    {totalRecords > 0 && (
                        <div className="card mt-3">
                            <div className="card-body">
                                <div className="pagination-wrap">
                                    <Pagination
                                        pageNo={pageNo}
                                        pageSize={pageSize}
                                        totalRecords={totalRecords}
                                        onPageChange={handlePageChange}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default EmployeeHistory;
